import { action, makeObservable, observable, runInAction } from 'mobx';
import * as yup from 'yup';
import { NotificationManager } from '~components/notification-manager';
import {
    HttpMethod,
    TranslationMap,
    CmsUserHistory,
    PaginatedList,
} from '~types/index';
import {
    ExternalGame,
    ExternalGamePayload,
    ExternalGameRequestCms,
} from '~types/games';
import { RootStore } from '~stores/root';
import { WithNetworkConcurrency } from '~stores/with-network-concurrency';

const YupGameVisibleProvince = yup.object({
    id: yup.number().required(),
    province: yup
        .object({
            id: yup.number().required(),
            name: yup.string().required(),
        })
        .required(),
});

const YupGames = yup.object({
    id: yup.number().required(),
    type: yup.string().required(),
    image: yup
        .object({
            fr: yup.string().required(),
            en: yup.string().required(),
        })
        .required(),
    title: yup
        .object({
            fr: yup.string().required(),
            en: yup.string().required(),
        })
        .required(),
    provinces: yup.array(YupGameVisibleProvince).nullable().optional(),
    // Optional on purpose: these only arrive from the paired API change, and
    // `fetchForSelect` is shared with featured-single-page. Requiring them
    // would make an API-before-FE gap fail validation for both pickers. They
    // feed the availability tag only.
    isActive: yup.boolean().optional(),
    startDate: yup.string().optional(),
    endDate: yup.string().optional(),
});

const YupVisibleGamesSelectList = yup.array(YupGames).required();

const YupVisibleGamesOrderItem = yup.object({
    id: yup.number().required(),
    type: yup.string().nullable().default('external'),
    image: yup.string().nullable().default(''),
    title: yup.string().nullable().default(''),
    subTitle: yup.string().nullable().default(''),
    status: yup.string().nullable().default(''),
    startDate: yup.string().nullable().default(null),
    endDate: yup.string().nullable().default(null),
    gameType: yup.string().nullable().default(null),
});

const YupVisibleGamesOrderList = yup.array(YupVisibleGamesOrderItem).required();

const YupGamesHistoryItem = yup.object({
    id: yup.number().required(),
    type: yup.string().required(),
    typeId: yup.number().nullable(),
    action: yup.string().required(),
    email: yup.string().nullable(),
    createdAt: yup.string().required(),
});

const YupGamesHistoryResponse = yup.object({
    page: yup.number().required(),
    perPage: yup.number().required(),
    total: yup.number().required(),
    results: yup.array(YupGamesHistoryItem).default([]),
});

export type GamesSearchParams = {
    [key in keyof ExternalGame]?: string;
};

export interface GameForSelectProvince {
    id: number;
    province: {
        id: number;
        name: string;
    };
}

export interface GameForSelect {
    id: number;
    type: string;
    image: TranslationMap;
    title: TranslationMap;
    provinces?: GameForSelectProvince[] | null;
    // Absent until the paired API change is deployed. See YupGames.
    isActive?: boolean;
    startDate?: string;
    endDate?: string;
}

export interface VisibleGameOrderItem {
    id: number;
    type: string;
    image: string | null;
    title: string;
    subTitle?: string | null;
    status?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    gameType?: string | null;
}

export const transformExternalGameToDTO = (
    game: ExternalGame
): ExternalGamePayload => {
    const { labattPromotions, ...gameDTO } = game;

    return {
        ...gameDTO,
        six12RegistrationUrl: gameDTO.six12RegistrationUrl || null,
        cmsUserHistory: gameDTO.cmsUserHistory || null,
        labattPromotions: labattPromotions,
    };
};

export class GamesStore extends WithNetworkConcurrency {
    @observable games: ExternalGame[] | null = null;
    @observable filteredGames: ExternalGame[] | undefined = undefined;
    @observable isFetchingCount = 0;
    @observable isUpsertingGame = false;
    @observable isDeletingGame = false;
    @observable isFetchingSingleGame = false;
    @observable isFetchingForSelect = false;
    @observable gamesForSelect: GameForSelect[] = [];

    @observable visibleGames: VisibleGameOrderItem[] = [];
    @observable isFetchingVisibleGames = false;
    @observable isUpdatingVisibleGamesOrder = false;

    @observable gamesHistory: PaginatedList<CmsUserHistory> | null = null;
    @observable isFetchingGamesHistory = false;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        super();
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action
    private setGames(
        this: GamesStore,
        games: ExternalGame[],
        searchParams: GamesSearchParams
    ) {
        this.games = games;
        this.filteredGames = games;

        if (searchParams) {
            this.filterGames(searchParams);
        }
    }

    @action
    async fetchGames(this: GamesStore, searchParams: GamesSearchParams) {
        const provinces = this.rootStore.applicationSelectionStore
            .getProvincesFromSelectedApplication;

        const tag = this.getTag();

        this.isFetchingCount++;

        const response = await this.rootStore.makeNetworkCall<ExternalGame[]>({
            method: HttpMethod.GET,
            url: '/games',
            params: {
                filteredProvinces: provinces,
            },
        });

        this.isFetchingCount--;

        if (!response.err && this.isLatestTag(tag)) {
            this.setGames(response.data, searchParams);
        }
    }

    @action
    async fetchSingleExternalGame(
        this: GamesStore,
        gameId: number
    ): Promise<ExternalGame> {
        runInAction(() => {
            this.isFetchingSingleGame = true;
        });

        const response = await this.rootStore.makeNetworkCall<ExternalGame>({
            method: HttpMethod.GET,
            url: `/games/external/${gameId}`,
        });

        runInAction(() => {
            this.isFetchingSingleGame = false;
        });

        if (!response.err) {
            return response.data;
        } else {
            throw response.err;
        }
    }

    @action
    async createGame(this: GamesStore, game: ExternalGameRequestCms) {
        this.isUpsertingGame = true;

        const gameDTO =
            game.type === 'external'
                ? transformExternalGameToDTO((game as unknown) as ExternalGame)
                : game;

        const response = await this.rootStore.makeNetworkCall<{
            id: number;
        }>({
            method: HttpMethod.POST,
            data: gameDTO,
            url: `/games`,
        });

        this.isUpsertingGame = false;

        if (!response.err) {
            NotificationManager.showSuccess('Game created');
            return response.data?.id ?? null;
        }

        return null;
    }

    @action
    async modifyGame(this: GamesStore, gamePayload: ExternalGameRequestCms) {
        this.isUpsertingGame = true;

        const gameDTO =
            gamePayload.type === 'external'
                ? transformExternalGameToDTO(
                      (gamePayload as unknown) as ExternalGame
                  )
                : gamePayload;

        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.PUT,
            data: gameDTO,
            url: `/games/${gamePayload.id}`,
        });

        this.isUpsertingGame = false;

        if (!response.err) {
            NotificationManager.showSuccess('Game modified');
        }
    }

    @action
    async deleteGame(this: GamesStore, gameType: string, gameId: number) {
        runInAction(() => {
            this.isDeletingGame = true;
        });

        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.DELETE,
            url: `/games/${gameType}/${gameId}`,
        });

        runInAction(() => {
            this.isDeletingGame = false;
        });

        if (!response.err) {
            NotificationManager.showSuccess(`Game id ${gameId} deleted.`);
        }
    }

    @action
    async fetchForSelect(this: GamesStore) {
        runInAction(() => {
            this.isFetchingForSelect = true;
        });
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/games/visible',
            },
            YupVisibleGamesSelectList
        );
        runInAction(() => {
            this.isFetchingForSelect = false;
        });

        if (!response.err) {
            runInAction(() => {
                this.gamesForSelect = response.data;
            });
        } else {
            throw response.err;
        }
    }

    @action
    async fetchVisibleGames(this: GamesStore, action?: string) {
        if (this.isFetchingVisibleGames) {
            return;
        }

        runInAction(() => {
            this.isFetchingVisibleGames = true;
        });
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/games/visible/order',
                params: action ? { action } : undefined,
            },
            YupVisibleGamesOrderList
        );
        runInAction(() => {
            this.isFetchingVisibleGames = false;
            if (!response.err) {
                this.visibleGames = response.data.map((g) => ({
                    ...g,
                    type: g.type ?? 'external',
                    title: g.title ?? '',
                    image: g.image ?? '',
                    subTitle: g.subTitle ?? '',
                    status: g.status ?? '',
                }));
            }
        });
    }

    @action
    setVisibleGames(this: GamesStore, games: VisibleGameOrderItem[]) {
        this.visibleGames = games;
    }

    @action
    async updateVisibleGamesOrder(this: GamesStore) {
        if (this.isUpdatingVisibleGamesOrder) {
            return;
        }

        runInAction(() => {
            this.isUpdatingVisibleGamesOrder = true;
        });
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.PATCH,
            url: '/games/sort',
            data: { gameIds: this.visibleGames.map((g) => g.id) },
        });
        runInAction(() => {
            this.isUpdatingVisibleGamesOrder = false;
        });

        if (!response.err) {
            NotificationManager.showSuccess('Visible games order updated');
        }

        return !response.err;
    }

    @action
    filterGames(gamesFilters: GamesSearchParams) {
        this.filteredGames = this.games?.filter((game) => {
            const keys = Object.keys(gamesFilters) as (keyof ExternalGame)[];

            for (const key of keys) {
                if (
                    (game[key] === undefined ||
                        game[key] !== gamesFilters[key]) &&
                    key !== 'title'
                ) {
                    return false;
                } else if (
                    key === 'title' &&
                    !game.title.en.includes(gamesFilters[key] as string)
                ) {
                    return false;
                }
            }
            return true;
        });
    }

    @action
    async fetchGamesHistory(page = 1, perPage = 10, action?: string) {
        if (this.isFetchingGamesHistory) {
            return;
        }

        runInAction(() => {
            this.isFetchingGamesHistory = true;
        });

        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/games/history',
                params: { page, perPage, ...(action ? { action } : {}) },
            },
            YupGamesHistoryResponse
        );

        runInAction(() => {
            this.isFetchingGamesHistory = false;
            if (!response.err && response.data) {
                this.gamesHistory = {
                    page: response.data.page,
                    perPage: response.data.perPage,
                    total: response.data.total,
                    results: response.data.results.map((item) => ({
                        email: item.email ?? null,
                        action: item.action,
                        createdAt: item.createdAt,
                    })),
                };
            }
        });
    }
}
